import React from "react";
import { fmtCurrency, fmtPct } from "../lib/formatters";
import { REFERENCE_ANCHORS, useDevMode } from "../lib/anchors";
import AnchorBadge from "./AnchorBadge";

// Funding Level: 4 metric lines + a horizontal red→yellow→green gradient bar
// with a blue marker showing where the current funding level sits.
//
// 2026-02-15 (Dave · horizon-context sensitivity) — the gauge is now
// driven by the horizon toggle at the top-right of the app instead of
// being locked to the final year of the stored projection. Data all
// comes from `calc` which already carries per-year values:
//   • calc.projection[i].ending          — ending cash at year i+1
//   • calc.full_funding_by_year[i]       — fully-funded target at year i+1
// If either array is missing (older cached calc, edge case), we fall
// back to the scalar `calc.funding_level_pct` / `calc.full_funding` so
// the panel is never blank.
//
// REVERT INSTRUCTIONS: to restore the previous "always show year-N of
// the projection" behavior, delete the `horizonIdx` / `endingAtHorizon`
// / `fullFundingAtHorizon` / `fundingLevel` local computations below
// and reinstate:
//     const fundingLevel = calc.funding_level_pct || 0;
//     const projectedEnding = calc.projection?.length
//       ? calc.projection[calc.projection.length - 1].ending : 0;
// Also revert the "Projected Balance @ …" label back to "Current Balance".
export default function FundingLevelGauge({ calc, variables, association, horizon = 30 }) {
  const [devMode] = useDevMode();
  if (!calc || !variables) return null;

  // Clamp horizon into [1, projection.length] so a mis-set toggle can
  // never index past the end of the array. `horizon` is 1-based years,
  // array is 0-based, so subtract 1.
  const projLen = calc.projection?.length || 0;
  const ffByYearLen = calc.full_funding_by_year?.length || 0;
  const maxYear = Math.min(
    horizon || 30,
    projLen || horizon,
    ffByYearLen || horizon,
  );
  const idx = Math.max(0, maxYear - 1);

  const endingAtHorizon = projLen > idx
    ? calc.projection[idx].ending
    : (calc.projection?.[projLen - 1]?.ending ?? 0);
  const fullFundingAtHorizon = ffByYearLen > idx
    ? calc.full_funding_by_year[idx]
    : (calc.full_funding || 0);
  const fundingLevel = fullFundingAtHorizon > 0
    ? (endingAtHorizon / fullFundingAtHorizon) * 100
    : 0;

  const clamped = Math.max(0, Math.min(100, fundingLevel));
  const targetPct = (variables.target_pct || 0) * 100;
  const clampedTarget = Math.max(0, Math.min(100, targetPct));
  const anchor = (association && REFERENCE_ANCHORS[association.aid]) || null;
  // Anchors are baselined at the FULL projection horizon (typically
  // year 30). Only show the drift badges when the user is viewing the
  // full horizon — otherwise the badge would falsely light up because
  // the shorter horizon legitimately differs from the anchor.
  const showAnchors = devMode && anchor && maxYear === projLen && projLen > 0;
  // Compute the calendar year for the "Projected Balance @ …" row so
  // Dave sees `2028` rather than `Yr 3` — reads naturally next to the
  // horizon toggle at the top-right.
  const endingYearLabel = association?.study_year
    ? `${association.study_year + maxYear - 1}`
    : `Yr ${maxYear}`;

  // 2026-02-15 (Dave) — Funding Level Shortfall row replaces the old
  // static "Target Level 40%" row (which was horizon-invariant and
  // therefore uninformative once the panel became horizon-aware).
  //   target_balance @ horizon = full_funding @ horizon × target_pct
  //   shortfall                = max(0, target − actual ending)
  // Same formula the Contributions tab + Funding Level Analysis chart
  // use, so the three surfaces agree to the dollar at any horizon.
  const targetBalanceAtHorizon =
    fullFundingAtHorizon * (variables.target_pct || 0);
  const shortfallAtHorizon = Math.max(
    0,
    targetBalanceAtHorizon - endingAtHorizon,
  );
  // 2026-02-15 (Dave) — Top row: swap "Full Funding" (which was
  // horizon-aware but always shown as 100% fully-funded) for the
  // Scenario's chosen target level at the current horizon. Numerator
  // now matches what the Funding Level % denominator SHOULD be under
  // the Scenario assumptions. `targetPctInt` is used only for the
  // label so mid-value percentages (e.g. 37.5%) still render cleanly.
  const targetPctInt = Math.round((variables.target_pct || 0) * 100);

  const rows = [
    {
      label: `Target Funding Level at ${targetPctInt}%`,
      value: fmtCurrency(targetBalanceAtHorizon),
      testid: "fl-full-funding",
      // Anchor badge intentionally omitted: the displayed value
      // (target-level = full_funding × target_pct) is no longer the
      // 100% figure that REFERENCE_ANCHORS is baselined against, so a
      // badge here would report phantom drift. Full-funding anchor
      // integrity is still checked via the Projected Balance row's
      // ending-balance badge + the Funding Level % ratio.
      badge: null,
    },
    {
      label: `Projected Balance @ ${endingYearLabel}`,
      value: fmtCurrency(endingAtHorizon),
      testid: "fl-current-balance",
      badge: showAnchors ? { actual: endingAtHorizon, expected: anchor.ending_balance, testid: "anchor-ending-balance" } : null,
    },
    {
      label: "Funding Level Shortfall",
      value: fmtCurrency(shortfallAtHorizon),
      testid: "fl-target-level",
    },
    {
      label: "Funding Level",
      value: fmtPct(fundingLevel, 0),
      testid: "fl-funding-level",
    },
  ];

  return (
    <div className="panel-compact flex flex-col" data-testid="funding-level-panel">
      <div className="dash-panel-header">
        <div className="text-[10.5px] tracking-[0.14em] uppercase font-bold text-navy">
          Funding Level
        </div>
        <div
          className="text-[10.5px] text-navy font-semibold font-mono tabular-nums"
          data-testid="funding-level-horizon-label"
        >
          {horizon} Year Horizon
        </div>
      </div>
      {/* Same 5-row grid + alignContent:'space-between' pattern as RAAG so
          the 4 metric rows sit at the same Y as RAAG rows 1-4 to the left.
          The 5th grid row hosts the risk band (col-span-2). */}
      <div
        className="grid grid-cols-[1fr_auto] gap-x-3 text-[10.5px] flex-1"
        style={{ alignContent: "space-between" }}
        data-testid="fl-rows"
      >
        {rows.map((r, idx) => {
          // Remove the divider under the last row ("Funding Level") so it
          // sits flush against the risk-band strip below.
          const showBorder = idx < rows.length - 1;

          // 2026-02-26 (Dave) — click-target rows (Funding Level %)
          // render as ONE button that spans BOTH grid columns so the
          // hover pill lands on the entire row's width, not just the
          // narrow value cell. Right-aligned label + value inside
          // keeps the visual identical to non-clickable rows.
          if (r.clickAction) {
            return (
              <button
                key={r.testid}
                type="button"
                onClick={() =>
                  window.dispatchEvent(
                    new Event(`ureserve:${r.clickAction}`),
                  )
                }
                title={r.clickHint}
                data-testid={`${r.testid}-open`}
                className="grid grid-cols-[1fr_auto] gap-x-3 items-center rounded-[3px] transition-colors text-left"
                style={{
                  gridColumn: "1 / -1",
                  cursor: "pointer",
                  background: "transparent",
                  border: 0,
                  // 2026-09-27 (Dave) — horizontal padding was pushing
                  // the "Funding Level" label 4 px to the right of the
                  // 3 non-clickable rows above it (which use `py-1`,
                  // i.e. zero horizontal padding). Zero-out padding-x
                  // so the label lands in the exact same column as
                  // "Full Funding Balance" et al.  Vertical breathing
                  // room retained.
                  padding: "4px 0",
                  ...(showBorder ? { borderBottom: "1px solid #C9D6E2", borderRadius: 0 } : {}),
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#EEF1F4";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
                onFocus={(e) => {
                  e.currentTarget.style.background = "#EEF1F4";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                <span
                  className="text-slate text-[10.5px] tracking-[0.08em] uppercase font-semibold"
                >
                  {r.label}
                </span>
                <span
                  className="font-mono tabular-nums text-ink text-right font-semibold"
                  data-testid={r.testid}
                >
                  {r.value}
                </span>
              </button>
            );
          }

          return (
          <React.Fragment key={r.testid}>
            <div
              className="text-slate text-[10.5px] tracking-[0.08em] uppercase font-semibold self-center py-1"
              style={showBorder ? { borderBottom: "1px solid #C9D6E2" } : undefined}
            >
              {r.label}
            </div>
            <div
              className="font-mono tabular-nums text-ink text-right font-semibold self-center py-1"
              data-testid={r.testid}
              style={showBorder ? { borderBottom: "1px solid #C9D6E2" } : undefined}
            >
              <div className="flex flex-col items-end gap-0.5">
                <span>{r.value}</span>
                {r.badge && (
                  <AnchorBadge
                    actual={r.badge.actual}
                    expected={r.badge.expected}
                    digits={r.badge.digits}
                    testid={r.badge.testid}
                  />
                )}
              </div>
            </div>
          </React.Fragment>
          );
        })}

        {/* Risk band — 5th grid row, spans both columns. Force the row's
            measured height to match a text row (~18.5 px) so alignContent:
            space-between distributes all 5 rows with the same rhythm as
            RAAG's 5 rows; the actual gradient + labels overflow visibly
            below into the panel's bottom padding. */}
        <div
          className="relative"
          style={{ gridColumn: "1 / -1", height: 28, overflow: "visible" }}
          title={`Funding level: ${fundingLevel.toFixed(1)}% · Target: ${targetPct.toFixed(0)}%`}
        >
          {/* top: -2 nudges bar + labels up 2px per Dave 2026-02-17 */}
          <div style={{ position: "absolute", top: -2, left: 0, right: 0 }}>
            <div
              data-testid="fl-gradient-bar"
              style={{
                height: 18, // 12 → 18 (+50%, Dave 2026-10-12)
                background:
                  "linear-gradient(to right, #EA6A47 0%, #EA6A47 33%, #F4D35E 33%, #F4D35E 67%, #83C88F 67%, #83C88F 100%)",
                border: "1px solid #C9D6E2",
              }}
            />
            {/* Target-level line — light-blue (sky), spans the exact height
                of the gradient bar (12 px), sits BEHIND the actual-funding
                marker so the marker wins on overlap. 2026-08-04 (Dave):
                switched from navy to sky per palette-consistency ask. */}
            <div
              data-testid="fl-target-line"
              title={`Target funding level: ${(targetPct).toFixed(1)}%`}
              style={{
                position: "absolute",
                top: 0,
                left: `calc(${clampedTarget}% - 1px)`,
                width: 2,
                height: 18,
                background: "#0091D5",
                pointerEvents: "none",
              }}
            />
            {/* Actual funding-level marker — 2026-08-04 (Dave v2): rounded
                rectangle with no fill (outline-only), taller than the
                gradient bar so it reads clearly. Navy outline for palette
                consistency. 2026-08-18 (Dave): trimmed width 14 → 10 px
                so the marker reads slimmer against the color bands
                without losing its click / tap target. Height + centering
                math updated to match. 2026-02-23 (Dave): border 2 → 1 px,
                width 10 → 9 px, height 24 → 23 px, top offset adjusted
                to keep the pill centered on the 12 px gradient. */}
            <div
              data-testid="fl-marker"
              title={`Funding level: ${fundingLevel.toFixed(1)}%`}
              style={{
                position: "absolute",
                top: -5,
                left: `calc(${clamped}% - 3.5px)`,
                width: 7,
                height: 28,
                background: "transparent",
                borderRadius: 4,
                border: "1px solid #1C4E80",
                boxShadow: "0 1px 2px rgba(0,0,0,0.18)",
                boxSizing: "border-box",
              }}
            />
            {/* Labels row: percentage ticks (0/33/66/100) aligned to the
                color-band intersections, with risk labels centered inside
                each band. All absolutely positioned so ticks land exactly
                under their gradient stops. Risk labels are bold to contrast
                against the italic-style tick values. */}
            <div
              className="relative mt-1 text-[9px] text-slate italic"
              style={{ height: 12 }}
              data-testid="fl-scale-labels"
            >
              <span style={{ position: "absolute", left: 0, top: 0 }}>0%</span>
              <span
                className="font-bold not-italic"
                style={{
                  position: "absolute",
                  left: "16.5%",
                  top: 0,
                  transform: "translateX(-50%)",
                }}
              >
                High Risk
              </span>
              <span style={{ position: "absolute", left: "33%", top: 0, transform: "translateX(-50%)" }}>
                33%
              </span>
              <span
                className="font-bold not-italic"
                style={{
                  position: "absolute",
                  left: "50%",
                  top: 0,
                  transform: "translateX(-50%)",
                }}
              >
                Moderate Risk
              </span>
              <span style={{ position: "absolute", left: "67%", top: 0, transform: "translateX(-50%)" }}>
                66%
              </span>
              <span
                className="font-bold not-italic"
                style={{
                  position: "absolute",
                  left: "83.5%",
                  top: 0,
                  transform: "translateX(-50%)",
                }}
              >
                Low Risk
              </span>
              <span style={{ position: "absolute", right: 0, top: 0 }}>100%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
